import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Activity, Palette, Brain, ArrowLeft, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BehavioralActivity from "@/components/activities-page/BehavioralActivity";
import HealthActivity from "@/components/activities-page/HealthActivity";
import CreativeActivity from "@/components/activities-page/CreativeActivity";
import SocialActivity from "@/components/activities-page/SocialActivity";
import ActivitySummaryPanel from "@/components/activities-page/ActivitySummaryPanel";
import AchievementBadges from "@/components/activities-page/AchievementBadges";
import ActivityHistory from "@/components/activities-page/ActivityHistory";
import AIActivitySuggestions from "@/components/activities-page/AIActivitySuggestions";

type ActiveView = "menu" | "behavioral" | "health" | "creative" | "social" | "ai";

interface ChildOption {
  id: string;
  full_name: string;
  nursery_id: string;
}

const SECTIONS = [
  { key: "behavioral" as const, label: "أنشطة سلوكية", emoji: "🤝", icon: Heart, desc: "تعاون، مشاركة، تعديل سلوك", gradient: "from-blue-100 to-indigo-100", border: "border-blue-300" },
  { key: "health" as const, label: "أنشطة صحية وحركية", emoji: "🏃", icon: Activity, desc: "تمارين، تحديات يومية، حركة", gradient: "from-green-100 to-emerald-100", border: "border-green-300" },
  { key: "creative" as const, label: "أنشطة إبداعية", emoji: "🎨", icon: Palette, desc: "رسم، قصص، مشاعر", gradient: "from-pink-100 to-rose-100", border: "border-pink-300" },
  { key: "social" as const, label: "أنشطة اجتماعية", emoji: "🫂", icon: Users, desc: "تواصل، تعاون، تعبير", gradient: "from-teal-100 to-cyan-100", border: "border-teal-300" },
  { key: "ai" as const, label: "تحليل ذكاء اصطناعي", emoji: "🧠", icon: Brain, desc: "اقتراحات وخطط مخصصة", gradient: "from-purple-100 to-violet-100", border: "border-purple-300" },
];

const MOTIVATIONAL_MESSAGES = [
  "🎉 أحسنت يا بطل!",
  "⭐ ممتاز! استمر!",
  "🌟 رائع جداً!",
  "💪 أنت نجم!",
  "🏆 إنجاز رائع!",
];

export default function Activities() {
  const { user, profile, roles } = useAuth();
  const [activeView, setActiveView] = useState<ActiveView>("menu");
  const [children, setChildren] = useState<ChildOption[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const [stats, setStats] = useState({ totalStars: 0, totalScore: 0, completedToday: 0, totalActivities: 0, streak: 0 });
  const [tab, setTab] = useState("activities");

  const isParent = roles.includes("parent");
  const nurseryId = profile?.nursery_id;

  useEffect(() => {
    fetchChildren();
  }, [user, nurseryId]);

  useEffect(() => {
    if (selectedChildId) fetchStats();
  }, [selectedChildId]);

  const fetchChildren = async () => {
    if (!user) return;
    let query = supabase.from("children").select("id, full_name, nursery_id");
    if (isParent) {
      query = query.eq("parent_id", user.id);
    } else if (nurseryId) {
      query = query.eq("nursery_id", nurseryId);
    }
    const { data } = await query.limit(100);
    if (data && data.length > 0) {
      setChildren(data);
      setSelectedChildId(data[0].id);
    }
  };

  const fetchStats = async () => {
    if (!selectedChildId) return;
    const today = new Date().toISOString().split("T")[0];

    const { data: allData } = await supabase
      .from("child_activities" as any)
      .select("score, stars, completed_at")
      .eq("child_id", selectedChildId)
      .order("completed_at", { ascending: false });

    const items = (allData || []) as any[];
    const todayItems = items.filter((a: any) => a.completed_at?.startsWith(today));

    // Calculate streak
    let streak = 0;
    const dates = [...new Set(items.map((a: any) => a.completed_at?.split("T")[0]).filter(Boolean))].sort().reverse();
    const d = new Date();
    for (const date of dates) {
      const expected = d.toISOString().split("T")[0];
      if (date === expected) {
        streak++;
        d.setDate(d.getDate() - 1);
      } else {
        break;
      }
    }

    setStats({
      totalStars: items.reduce((s: number, a: any) => s + (a.stars || 0), 0),
      totalScore: items.reduce((s: number, a: any) => s + (a.score || 0), 0),
      completedToday: todayItems.length,
      totalActivities: items.length,
      streak,
    });
  };

  const handleActivityComplete = async (score: number, stars: number, activityKey: string) => {
    const child = children.find(c => c.id === selectedChildId);
    if (!child) return;

    const activityType = activityKey.startsWith("behavioral") ? "behavioral"
      : activityKey.startsWith("health") ? "health"
      : activityKey.startsWith("social") ? "social"
      : "creative";

    const activityNames: Record<string, string> = {
      behavioral_scenarios: "سيناريوهات سلوكية",
      health_challenges: "تحديات حركية",
      creative_story: "إكمال قصة",
      creative_colors: "عالم الألوان",
      creative_feelings: "ارسم مشاعرك",
      creative_coloring: "التلوين التفاعلي",
      creative_matching: "مطابقة الصور",
      creative_puzzle: "تركيب الأشكال",
      social_scenarios: "أنشطة اجتماعية",
    };

    try {
      await supabase.from("child_activities" as any).insert({
        nursery_id: child.nursery_id,
        child_id: selectedChildId,
        activity_type: activityType,
        activity_key: activityKey,
        activity_name: activityNames[activityKey] || activityKey,
        score,
        stars,
      } as any);
      fetchStats();
      const msg = MOTIVATIONAL_MESSAGES[Math.floor(Math.random() * MOTIVATIONAL_MESSAGES.length)];
      toast.success(`${msg} حصلت على ${stars} نجوم!`);
    } catch (e) {
      console.error(e);
    }
  };

  const selectedChild = children.find(c => c.id === selectedChildId);

  // Activity views
  if (activeView === "behavioral") {
    return (
      <div className="p-4 max-w-2xl mx-auto animate-fade-in">
        <BehavioralActivity onComplete={handleActivityComplete} onBack={() => setActiveView("menu")} />
      </div>
    );
  }
  if (activeView === "health") {
    return (
      <div className="p-4 max-w-2xl mx-auto animate-fade-in">
        <HealthActivity onComplete={handleActivityComplete} onBack={() => setActiveView("menu")} />
      </div>
    );
  }
  if (activeView === "creative") {
    return (
      <div className="p-4 max-w-2xl mx-auto animate-fade-in">
        <CreativeActivity onComplete={handleActivityComplete} onBack={() => setActiveView("menu")} />
      </div>
    );
  }
  if (activeView === "social") {
    return (
      <div className="p-4 max-w-2xl mx-auto animate-fade-in">
        <SocialActivity onComplete={handleActivityComplete} onBack={() => setActiveView("menu")} />
      </div>
    );
  }
  if (activeView === "ai") {
    return (
      <div className="p-4 max-w-2xl mx-auto space-y-4 animate-fade-in">
        <Button onClick={() => setActiveView("menu")} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        {selectedChild && (
          <AIActivitySuggestions childId={selectedChildId} childName={selectedChild.full_name} />
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-foreground">🌈 الأنشطة التكميلية</h1>
        <p className="text-muted-foreground">أنشطة لبناء شخصية الطفل: سلوكية، صحية، إبداعية، اجتماعية</p>
      </div>

      {/* Child selector */}
      {children.length > 0 && (
        <div className="flex items-center gap-3 justify-center">
          <span className="text-sm font-medium text-foreground">اختر الطفل:</span>
          <Select value={selectedChildId} onValueChange={setSelectedChildId}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {children.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Summary */}
      <ActivitySummaryPanel {...stats} />

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="activities">🎮 الأنشطة</TabsTrigger>
          <TabsTrigger value="badges">🏆 الشارات</TabsTrigger>
          <TabsTrigger value="history">📋 السجل</TabsTrigger>
        </TabsList>

        <TabsContent value="activities" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECTIONS.map((section, i) => (
              <Card
                key={section.key}
                className={`cursor-pointer hover:shadow-xl transition-all border-2 ${section.border} hover:scale-[1.02] animate-fade-in`}
                style={{ animationDelay: `${i * 80}ms` }}
                onClick={() => setActiveView(section.key)}
              >
                <CardContent className={`p-6 bg-gradient-to-br ${section.gradient} rounded-lg`}>
                  <div className="text-center space-y-3">
                    <div className="text-5xl">{section.emoji}</div>
                    <h3 className="text-lg font-bold text-foreground">{section.label}</h3>
                    <p className="text-sm text-muted-foreground">{section.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="badges" className="mt-4">
          <AchievementBadges
            totalStars={stats.totalStars}
            totalScore={stats.totalScore}
            totalActivities={stats.totalActivities}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {selectedChildId && <ActivityHistory childId={selectedChildId} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
